import { useCallback, useMemo, useState } from "react";

export function useDeferredFilters<T>(
  getDefaultFilters: () => T,
  options?: {
    onApply?: (applied: T) => void;
    onReset?: (applied: T) => void;
  },
) {
  const [formFilters, setFormFiltersState] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);

  const setFormFilters = useCallback((filters: T) => {
    setFormFiltersState(filters);
  }, []);

  const updateFormField = useCallback(
    <K extends keyof T>(field: K, value: T[K]) => {
      setFormFiltersState((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const mergeFormFields = useCallback((partial: Partial<T>) => {
    setFormFiltersState((prev) => ({ ...prev, ...partial }));
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedFilters(formFilters);
    options?.onApply?.(formFilters);
  }, [formFilters, options]);

  /** Apply a partial change immediately (quick filters), keeping the form in sync. */
  const applyFilterPatch = useCallback(
    (partial: Partial<T>) => {
      const next = { ...appliedFilters, ...partial };
      setFormFiltersState((prev) => ({ ...prev, ...partial }));
      setAppliedFilters(next);
      options?.onApply?.(next);
    },
    [appliedFilters, options],
  );

  const resetFilters = useCallback(() => {
    const defaults = getDefaultFilters();
    setFormFiltersState(defaults);
    setAppliedFilters(defaults);
    options?.onReset?.(defaults);
  }, [getDefaultFilters, options]);

  const hasUnappliedChanges = useMemo(
    () => JSON.stringify(formFilters) !== JSON.stringify(appliedFilters),
    [formFilters, appliedFilters],
  );

  return {
    formFilters,
    appliedFilters,
    setFormFilters,
    updateFormField,
    mergeFormFields,
    applyFilters,
    applyFilterPatch,
    resetFilters,
    hasUnappliedChanges,
  };
}
